import { supabase } from "@/lib/supabase/client";

export interface TenantSummary {
  id: string;
  name: string;
  slug: string;
  plan: "solo" | "mid_market" | "multi_fund" | "enterprise";
  region: "us" | "eu";
}

export interface TenantMembership {
  tenant_id: string;
  role: "owner" | "admin" | "reviewer" | "viewer";
  tenant: Omit<TenantSummary, "region">;
}

/**
 * Returns the tenants the signed-in user is a member of.
 * RLS lets members see co-members' rows too (owners manage members), so the
 * explicit user_id filter is required — without it every co-member duplicates
 * the tenant card.
 */
export async function fetchTenantMemberships(): Promise<TenantMembership[]> {
  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError || !userData.user) throw new Error("Not signed in.");

  const { data, error } = await supabase
    .from("tenant_members")
    .select("tenant_id, role, tenant:tenants!inner(id, name, slug, plan)")
    .eq("user_id", userData.user.id)
    .not("accepted_at", "is", null)
    .order("invited_at", { ascending: true });

  if (error) throw new Error(`fetchTenantMemberships: ${error.message}`);
  return (data ?? []) as unknown as TenantMembership[];
}

export async function fetchTenantBySlug(slug: string): Promise<TenantSummary | null> {
  const { data, error } = await supabase
    .from("tenants")
    .select("id, name, slug, plan, region")
    .eq("slug", slug)
    .maybeSingle();
  if (error) throw new Error(`fetchTenantBySlug: ${error.message}`);
  return data;
}
